// angular dependencies
import { inject } from '@angular/core';

// third-party dependencies
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  concatMap,
  filter,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';

// fantaz dependencies
import { AuthSelectors, getAuthUser } from '@fantaz/shared/auth';
import { CoreRouterActions } from '@fantaz/core/router';
import { CoreSnackbarActions } from '@fantaz/core/state';
import {
  ContactPointSelector,
  ProfileActions,
} from '@fantaz/profile/data-access';
import {
  AuthService,
  AuthServiceInjectionToken,
} from '@fantaz/shared/data-access/api';
import {
  SendUserAttributeVerificationCodeInput,
  UpdateUserAttributeInput,
  VerifiableUserAttributeKey,
} from '@fantaz/shared/data-access/models';
import {
  ContactPointActionTypes,
  ContactPointFormActionTypes,
  ContactPointResendVerifyCodeActionTypes,
  ContactPointConfirmVerificationCodeActions,
} from './contact-point.actions';

export class ContactPointEffects {
  #actions$: Actions = inject(Actions);
  #store: Store = inject(Store);
  #authService: AuthService = inject(AuthServiceInjectionToken);

  /**
   * Effect to validate a contact point, check if it is already in use by another
   * user or not valid for the system to use as a contact point for the user
   * profile.
   * If the contact point is valid, it will call the initVerification action
   */
  validateContactPointEffect$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(ContactPointFormActionTypes.savingContactPointInProgress),
      switchMap((payload) =>
        this.#authService.checkUsername(payload).pipe(
          mergeMap(() => [
            ContactPointFormActionTypes.savingContactPointSuccess(payload),
            ContactPointFormActionTypes.initVerification(payload),
          ]),
          catchError((error) => [
            ContactPointFormActionTypes.savingContactPointFailure({
              ...payload,
              error,
            }),
          ]),
        ),
      ),
    ),
  );

  /**
   * Effect to redirect to verify contact point page when contact point is valid.
   */
  redirectToVerifyContactPointEffect$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(ContactPointFormActionTypes.initVerification),
      map(({ attr }) =>
        CoreRouterActions.navigate(
          [
            '',
            {
              outlets: {
                dialog: ['verify-contact-point', attr],
              },
            },
          ],
          {
            queryParamsHandling: 'preserve',
          },
        ),
      ),
    ),
  );

  /**
   * Effect to save a contact point to user
   */
  savingContactPointEffect$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(ContactPointActionTypes.addContactPointInProgress),
      withLatestFrom(
        this.#store.select(ContactPointSelector.selectAttr),
        this.#store.select(ContactPointSelector.selectAttrValue),
        this.#store.select(getAuthUser),
        (_, attr, value, user) => {
          const result: any = {
            attributes: {
              attr: attr as string,
              value: value as string,
            },
          };
          if (user) {
            result.clientMetadata = { email: user?.email };
          }
          return { data: result, user };
        },
      ),
      switchMap((payload) => {
        let custom = false;
        if (payload.data.attributes.attr == 'email' && payload.user) {
          custom = payload.user.email === payload.data.attributes.value;
        } else if (
          payload.data.attributes.attr == 'phone_number' &&
          payload.user
        ) {
          custom = payload.user?.phone === payload.data.attributes.value;
        }
        return this.#authService
          .updateUserAttribute(
            {
              userAttribute: {
                attributeKey: payload.data.attributes.attr,
                value: payload.data.attributes.value,
              },
            },
            { custom },
          )
          .pipe(
            map(() =>
              ContactPointActionTypes.addContactPointSuccess({
                attr: payload.data.attr,
                value: payload.data.value,
              }),
            ),
            catchError((error) => [
              ContactPointActionTypes.addContactPointFailure({
                attr: payload.data.attr,
                value: payload.data.value,
                error,
              }),
            ]),
          );
      }),
    ),
  );

  /**
   * Effect to verify a contact point
   */
  verifyContactPointEffect$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(
        ContactPointConfirmVerificationCodeActions.confirmVerificationCodeInProgress,
      ),
      withLatestFrom(this.#store.select(getAuthUser)),
      switchMap(([{ attr, value, code }, user]) => {
        let custom = false;
        if (attr == 'email' && user) {
          custom = user.email === value;
        } else if (attr == 'phone_number' && user) {
          custom = user.phone === value;
        }
        return this.#authService
          .confirmUserAttribute(
            {
              confirmationCode: code,
              userAttributeKey: attr as any,
            },
            { custom },
          )
          .pipe(
            concatMap(() => [
              // navigate to verify contact point
              CoreRouterActions.navigate(
                [
                  '',
                  {
                    outlets: {
                      dialog: null,
                    },
                  },
                ],
                {
                  queryParamsHandling: 'preserve',
                },
              ),

              // show snackbar message when contact point is verified
              CoreSnackbarActions.showSnackbar({
                message: `Your ${
                  attr == 'email' ? 'email address' : 'phone number'
                } has been successfully verified.`,
                config: {
                  duration: 5000,
                  dismissible: true,
                },
              }),

              // update effect state
              ContactPointConfirmVerificationCodeActions.confirmVerificationCodeSuccess(
                {
                  attr,
                  code,
                  value,
                },
              ),
            ]),
            // refresh session when contact point is verified to update user's
            // profile with the verified contact point
            tap(() => {
              this.#authService.refreshSession();
            }),
            catchError((error) => [
              ContactPointConfirmVerificationCodeActions.confirmVerificationCodeFailure(
                {
                  attr,
                  code,
                  value,
                  error,
                },
              ),
            ]),
          );
      }),
    ),
  );

  resendVerifyCodeEffect$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(
        ContactPointResendVerifyCodeActionTypes.resendVerifyCodeInProgress,
      ),
      withLatestFrom(
        this.#store.select(ContactPointSelector.selectAttr),
        this.#store.select(ContactPointSelector.selectAttrValue),
        this.#store.select(getAuthUser),
        (_, attr, value, user) => {
          const result: UpdateUserAttributeInput = {
            userAttribute: {
              attributeKey: attr as VerifiableUserAttributeKey,
              value: value as string,
            },
            options: {
              clientMetadata: {
                ...(user && user.email
                  ? {
                      email: user?.email as string,
                    }
                  : {}),
              },
            },
          };
          return { data: result, user };
        },
      ),
      switchMap((payload) => {
        let custom = false;
        if (
          payload.data.userAttribute.attributeKey == 'email' &&
          payload.user
        ) {
          custom = payload.user.email === payload.data.userAttribute.value;
        } else if (
          payload.data.userAttribute.attributeKey == 'phone_number' &&
          payload.user
        ) {
          custom = payload.user?.phone === payload.data.userAttribute.value;
        }
        return this.#authService
          .updateUserAttribute(
            {
              ...payload.data,
            },
            { custom },
          )
          .pipe(
            mergeMap(() => [
              ContactPointResendVerifyCodeActionTypes.resendVerifyCodeSuccess(
                payload.data,
              ),
              CoreSnackbarActions.showSnackbar({
                message: `A new verification code has been sent.`,
                config: {
                  duration: 5000,
                  dismissible: true,
                },
              }),
            ]),
          );
      }),
    ),
  );

  // profile

  /**
   * Effect to save a contact point to the user's profile when user not have
   * their contact point in their profile
   */
  saveContactPointToProfileEffect$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(ContactPointActionTypes.addContactPointSuccess),
      withLatestFrom(this.#store.select(AuthSelectors.selectUser)),
      filter(([{ attr }, currentUser]) => {
        if (currentUser && !currentUser.email && attr == 'email') {
          return true;
        }

        if (currentUser && !currentUser.phone && attr == 'phone_number') {
          return true;
        }

        return false;
      }),
      mergeMap(([{ attr, value }]) => [
        ProfileActions.updateUserContactAttribute({
          payload: {
            [attr]: value,
          },
        }),
      ]),
    ),
  );

  /**
   * Effect to save a contact point to the user's profile when contact point is
   * verified
   */
  updateContactPointToProfileEffect$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(
        ContactPointConfirmVerificationCodeActions.confirmVerificationCodeSuccess,
      ),
      withLatestFrom(this.#store.select(AuthSelectors.selectUser)),
      filter(([_, currentUser]) => !!currentUser),
      mergeMap(([{ attr, value }]) => [
        ProfileActions.updateUserContactAttribute({
          payload: {
            [attr]: value,
          },
        }),
      ]),
    ),
  );

  // This effect listens for the 'addContactPointSuccess' action.
  // When that action is dispatched, it triggers the 'loadProfile' action.
  // This is typically used to refresh the profile data after a contact point has been successfully added.
  addContactPointSuccess$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(ContactPointActionTypes.addContactPointSuccess),
      mergeMap(() => [ProfileActions.loadProfile()]),
    ),
  );
}
